/**
 * Throbbing "x active alerts" badge for product header / list cards.
 * Color + icon follow the highest-severity active alert; hovering shows a
 * rich portal card (duration + ongoing styling) via the shared AlertSummaryCard.
 */
import * as React from "react";
import * as ReactDOM from "react-dom";
import { AlertTriangle, Bell } from "lucide-react";
import { severityColor } from "../alerts/alertDisplay";
import { AlertSummaryCard } from "../alerts/AlertSummaryCard";
import type { ProductAlertEpisode } from "../../lib/api/products";
import * as s from "./ActiveAlertsCountBadge.css";

export type ActiveAlertSummary = {
  code: string;
  name?: string | null;
  type?: string | null;
  severity?: number | null;
  /** ISO timestamp this code was first observed open — threaded through the WS
   *  payload (product-service Part 1) so the tooltip can show real duration. */
  openedAt?: string | null;
};

export function highestSeverityAlert(
  alerts: ActiveAlertSummary[],
): ActiveAlertSummary | null {
  if (alerts.length === 0) return null;
  return alerts.reduce((best, a) => {
    const sev = Math.min(5, Math.max(1, a.severity ?? 1));
    const bestSev = Math.min(5, Math.max(1, best.severity ?? 1));
    return sev >= bestSev ? a : best;
  });
}

export const severityStyle = severityColor;

export function formatActiveAlertsLabel(count: number): string {
  return count === 1 ? "1 active alert" : `${count} active alerts`;
}

function parseSeverity(raw: unknown): number | null {
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string") {
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
    const named: Record<string, number> = {
      info: 1,
      suggestion: 1,
      low: 2,
      warning: 3,
      medium: 3,
      high: 4,
      error: 4,
      critical: 5,
    };
    return named[raw.toLowerCase()] ?? null;
  }
  return null;
}

/** Extract active alert summaries from a telemetry snapshot measures object. */
export function activeAlertsFromMeasures(
  measures:
    | Record<
        string,
        {
          codes?: Array<{
            code?: string;
            label?: string;
            type?: string;
            severity?: string | number | null;
            openedAt?: string | null;
          }>;
        }
      >
    | null
    | undefined,
): ActiveAlertSummary[] {
  if (!measures) return [];
  const out: ActiveAlertSummary[] = [];
  const seen = new Set<string>();
  for (const m of Object.values(measures)) {
    if (!m?.codes?.length) continue;
    for (const c of m.codes) {
      if (!c?.code) continue;
      const upper = String(c.code).toUpperCase();
      if (seen.has(upper)) continue;
      seen.add(upper);
      out.push({
        code: c.code,
        name: c.label ?? c.code,
        type: c.type ?? null,
        severity: parseSeverity(c.severity),
        openedAt: c.openedAt ?? null,
      });
    }
  }
  return out;
}

export function ActiveAlertSeverityIcon({
  type,
  size = 12,
  className,
}: {
  type?: string | null;
  size?: number;
  className?: string;
}) {
  const Icon = type === "fault" ? AlertTriangle : Bell;
  return (
    <span className={[s.iconWrap, className].filter(Boolean).join(" ")} aria-hidden>
      <span className={s.pulseRing} />
      <Icon size={size} strokeWidth={2.25} className={s.iconGlyph} />
    </span>
  );
}

function toCardEpisodes(alerts: ActiveAlertSummary[]): ProductAlertEpisode[] {
  return alerts.map((a) => ({
    // This badge's WS-derived summary has no real incident id — this display-only
    // list (AlertSummaryCard) never uses incidentId for anything actionable.
    incidentId: a.code,
    code: a.code,
    name: a.name ?? a.code,
    type: a.type ?? null,
    severity: a.severity ?? null,
    description: null,
    openedAt: a.openedAt ?? new Date().toISOString(),
    closedAt: null,
    active: true,
    resolvedSource: null,
    resolvedByUserId: null,
    resolvedByUserName: null,
    resolutionNote: null,
  }));
}

export function ActiveAlertsCountBadge({
  alerts,
  className,
}: {
  alerts: ActiveAlertSummary[];
  className?: string;
}) {
  const wrapperRef = React.useRef<HTMLSpanElement>(null);
  const [tooltipVisible, setTooltipVisible] = React.useState(false);
  const [tooltipPos, setTooltipPos] = React.useState<{ bottom: number; left: number } | null>(null);

  const showTooltip = React.useCallback(() => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setTooltipPos({
        bottom: window.innerHeight - rect.top + 8,
        left: rect.left + rect.width / 2,
      });
    }
    setTooltipVisible(true);
  }, []);
  const hideTooltip = React.useCallback(() => setTooltipVisible(false), []);

  if (alerts.length === 0) return null;
  const top = highestSeverityAlert(alerts);
  const colors = severityStyle(top?.severity);
  const label = formatActiveAlertsLabel(alerts.length);

  const tooltipEl = tooltipVisible && tooltipPos ? (
    <div
      className={s.tooltipPortal}
      style={{ position: "fixed", bottom: tooltipPos.bottom, left: tooltipPos.left, transform: "translateX(-50%)" }}
      role="tooltip"
    >
      <div className={s.tooltipInner}>
        <AlertSummaryCard alerts={toCardEpisodes(alerts)} />
      </div>
    </div>
  ) : null;

  return (
    <>
      <span
        ref={wrapperRef}
        className={[s.badge, className].filter(Boolean).join(" ")}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        style={{
          color: colors.fg,
          background: colors.bg,
          borderColor: colors.border,
        }}
      >
        <ActiveAlertSeverityIcon type={top?.type} />
        {label}
      </span>
      {typeof document !== "undefined" && ReactDOM.createPortal(tooltipEl, document.body)}
    </>
  );
}
